import React, { forwardRef } from 'react';

type Size = 'md' | 'lg';

interface Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  inputSize?: Size;
  hasError?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

const sizeClasses: Record<Size, string> = {
  md: 'h-10 text-sm',
  lg: 'h-11 text-base',
};

const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      inputSize = 'lg',
      hasError = false,
      leadingIcon,
      trailingIcon,
      className = '',
      ...rest
    },
    ref
  ) => {
    const padLeft = leadingIcon ? 'pl-10' : 'pl-3.5';
    const padRight = trailingIcon ? 'pr-10' : 'pr-3.5';

    const inputClasses = [
      'block w-full rounded-lg border bg-white text-slate-900 placeholder:text-slate-400',
      'transition-colors duration-150 outline-none',
      'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
      sizeClasses[inputSize],
      padLeft,
      padRight,
      hasError
        ? 'border-rose-400 focus:border-rose-600 focus:ring-2 focus:ring-rose-600/20'
        : 'border-slate-300 focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20',
      className,
    ].join(' ');

    if (!leadingIcon && !trailingIcon) {
      return <input ref={ref} className={inputClasses} {...rest} />;
    }

    return (
      <div className="relative">
        {leadingIcon && (
          <span
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          >
            {leadingIcon}
          </span>
        )}
        <input ref={ref} className={inputClasses} {...rest} />
        {trailingIcon && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
            {trailingIcon}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
